import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import Div100vh from "react-div-100vh";

import { useTheme } from "@/b2b/context/ThemeContext";
import { handleAndTrackError, mergeAddress } from "@/lib/helper";
import { SquareUser, MapPinHouse, CalendarDays, MapPin } from "lucide-react";
import config from "@/config";
import Header from "@/b2b/components/Header";
import ProcessingScreen from "@/b2b/pages/processing";
import Dialog from "./dialog";

const KYCDetail = ({ icon: Icon, label, value, theme }) => (
  <div className="flex items-center gap-4">
    <Icon
      className={` ${theme?.darkTextColor && theme.darkTextColor} max-w-4`}
    />
    <div className="flex flex-col gap-1">
      <span
        className={`text-base text-[#999999] ${
          theme?.darkTextColor && theme.darkTextColor
        }`}
      >
        {label}
      </span>
      <span className={`${theme?.textColor && theme?.textColor} text-3xs`}>
        {value}
      </span>
    </div>
  </div>
);

export default function KYC() {
  const theme = useTheme();

  const location = useLocation();
  const { data, kycRefId, lender } = location.state || {};
console.log("KYC Data:", data, "KYC Ref ID:", kycRefId);
  // FEXPRIME journeys reach this same screen but talk to a different backend: the
  // `/ekyc/fexpex/*` routes proxy to the lender, whereas the native routes below drive
  // cpcore's own Digitap integration. Absent `lender` (every pre-existing caller) keeps
  // the native path exactly as it was.
  const isFexprime = lender === "FEXPRIME";
  const { name, father_name, age, photo, kyc_date, ckyc_ref_id } = data || {};

  const [isLoading, setIsLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [otp, setOtp] = useState("");
  const [isResending, setIsResending] = useState(false);
  const [requestId, setRequestId] = useState(null);
  const [ckycResponseRequestId, setCkycResponseRequestId] = useState(null);
  const [dialogMessage, setDialogMessage] = useState("");
  const [isYesLoading, setIsYesLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  /**
   * FEXPRIME: "Yes, it's me" — ask the lender to send the CKYC download OTP.
   *
   * No `ref_id` is sent. cpcore derives it from the caller's own journey, because fexpex's
   * `/kyc/*` routes have no auth guard and resolve the CKYC record by `ref_id` alone — a
   * client-supplied ref would let one tenant drive another's download.
   *
   * fexpex answers `{ data: <digitap payload>, status, message }`; the vendor ids we need to
   * submit the OTP live on the inner object.
   */
  const fexprimeConfirmIdentity = async () => {
    try {
      const response = await fetch(`${config.API_URL}/ekyc/fexpex/verify-ckyc`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem(
            config.LOCAL_KEYS.SIGNUP_TOKEN
          )}`,
        },
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        handleAndTrackError(
          new Error(body?.message || "Failed to send the CKYC OTP."),
          {
            apiPath: "/ekyc/fexpex/verify-ckyc",
            responseStatus: response.status,
            responseError: body,
          }
        );
        return;
      }

      const vendor = body?.data || body;
      const otpSent = String(vendor?.error || body?.message || "")
        .toLowerCase()
        .includes("otp");

      if (!otpSent) {
        // The lender could not start the download (commonly "record does not exist").
        // Declining is the honest next step — it cancels CKYC and starts the mandatory eKYC.
        await fexprimeRejectIdentity();
        return;
      }

      setRequestId(vendor?.request_id || vendor?.client_ref_num || kycRefId);
      setCkycResponseRequestId(vendor?.ckyc_response_request_id);
      setDialogMessage(vendor?.error || "");
      setIsDialogOpen(true);
    } catch (error) {
      handleAndTrackError(error, { apiPath: "/ekyc/fexpex/verify-ckyc" });
    }
  };

  /**
   * FEXPRIME: "No, this is not me" — a fully supported outcome, not a failure. CKYC is
   * shown but never required; cpcore cancels it and starts the mandatory eKYC, and the
   * answer carries the Digio redirect.
   */
  const fexprimeRejectIdentity = async () => {
    try {
      const response = await fetch(`${config.API_URL}/ekyc/fexpex/not-me`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem(
            config.LOCAL_KEYS.SIGNUP_TOKEN
          )}`,
        },
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok || !body?.sessionUrl) {
        handleAndTrackError(
          new Error(body?.message || "Failed to start the KYC session."),
          {
            apiPath: "/ekyc/fexpex/not-me",
            responseStatus: response.status,
            responseError: body,
          }
        );
        return;
      }
      window.location.href = body.sessionUrl;
    } catch (error) {
      handleAndTrackError(error, { apiPath: "/ekyc/fexpex/not-me" });
    }
  };

  const clickKycAction = async (newKyc = false) => {
    try {
      const response = await fetch(
        `${config.API_URL}/${newKyc ? "ekyc/start-ekyc" : "ekyc/verify-ckyc"}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem(
              config.LOCAL_KEYS.SIGNUP_TOKEN
            )}`,
          },
          body: JSON.stringify({kycRefId: kycRefId }),
          
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage =
          errorData.message || "Failed to start KYC session.";
        const error = new Error(errorMessage);

        handleAndTrackError(error, {
          apiPath: "/ekyc/verify-ckyc",
          responseStatus: response.status,
          responseError: errorData,
        });
      }

      const data = await response.json();
      console.log("KYC Response Data:", data);
      if(data && data?.event === "OTP_SENT") {
        
        setDialogMessage(data?.message || "");
        setIsDialogOpen(true);
      }

      else if(data && data?.event === "FULL_KYC_INITIATED") {
        window.location.href = data?.data?.sessionUrl 
      }
      else {
        window.location.href = data?.data?.sessionUrl ;

      }
      

    } catch (error) {
      handleAndTrackError(error, {
        apiPath: "/ekyc/verify-ckyc",
      });
    }
  };

  const handleClickYes = async () => {
    try {
      setIsYesLoading(true); // Only button loading
      if (isFexprime) return await fexprimeConfirmIdentity();
      await clickKycAction(); // Wait for the action to complete
    } catch (error) {
      console.error("KYC API Failed.");
    } finally {
      setIsYesLoading(false);
    }
  };

  const handleClickNo = async () => {
    setIsLoading(true); // Disable buttons
    if (isFexprime) {
      await fexprimeRejectIdentity();
      setIsLoading(false);
      return;
    }
    await clickKycAction(true); // Wait for the action to complete
    setIsLoading(false); // Re-enable buttons (if needed)
  };

  /**
   * FEXPRIME: submit the CKYC OTP.
   *
   * On success cpcore starts the mandatory eKYC SERVER-SIDE and answers with the Digio
   * redirect. That hand-off used to live in an 8-second browser `setTimeout` on the old pods
   * page — close the tab inside that window and eKYC never started, which is why some
   * borrowers today have a completed CKYC and no eKYC at all.
   *
   * A rejected OTP comes back as `{ action: "CKYC_OTP_REJECTED" }` and must leave the dialog
   * open so the tenant can retry, rather than silently moving them on.
   */
  const fexprimeSubmitOtp = async () => {
    try {
      const response = await fetch(
        `${config.API_URL}/ekyc/fexpex/submit-ckyc-otp`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem(
              config.LOCAL_KEYS.SIGNUP_TOKEN
            )}`,
          },
          body: JSON.stringify({
            otp,
            request_id: requestId,
            ckyc_response_request_id: ckycResponseRequestId,
          }),
        }
      );
      const body = await response.json().catch(() => ({}));

      if (!response.ok || body?.action === "CKYC_OTP_REJECTED") {
        setDialogMessage(
          body?.result?.message || body?.message || "Invalid OTP. Please try again."
        );
        return;
      }

      setIsDialogOpen(false);
      setOtp("");

      if (body?.sessionUrl) {
        window.location.href = body.sessionUrl;
        return;
      }
      // CKYC completed but no eKYC redirect came back. eKYC is mandatory, so ask for it
      // rather than leaving the tenant on a dead screen.
      await fexprimeRejectIdentity();
    } catch (error) {
      handleAndTrackError(error, { apiPath: "/ekyc/fexpex/submit-ckyc-otp" });
    } finally {
      setIsSubmitting(false);
    }
  };

  /** FEXPRIME: re-send the CKYC OTP. fexpex enforces its own ~90s cooldown. */
  const fexprimeResendOtp = async () => {
    try {
      const response = await fetch(
        `${config.API_URL}/ekyc/fexpex/resend-ckyc-otp`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem(
              config.LOCAL_KEYS.SIGNUP_TOKEN
            )}`,
          },
          body: JSON.stringify({
            request_id: requestId,
            ckyc_response_request_id: ckycResponseRequestId,
          }),
        }
      );
      const body = await response.json().catch(() => ({}));
      setDialogMessage(
        body?.data?.error || body?.message || "OTP resent to your registered mobile number."
      );
    } catch (error) {
      handleAndTrackError(error, { apiPath: "/ekyc/fexpex/resend-ckyc-otp" });
    } finally {
      setIsResending(false);
    }
  };

  const handleOtpSubmit = async () => {
    try {
     
      setIsSubmitting(true);
      if (isFexprime) return await fexprimeSubmitOtp();
      console.log(ckycResponseRequestId, requestId, otp);
      const response = await fetch(`${config.API_URL}/ekyc/submit-ckyc-otp`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem(
            config.LOCAL_KEYS.SIGNUP_TOKEN
          )}`,
        },
        body: JSON.stringify({
          otp,
          kycRefId: kycRefId,
         
        }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        handleAndTrackError(
          new Error(errorData.message || "OTP verification failed"),
          {
            apiPath: "/ekyc/submit-ckyc-otp",
            responseStatus: response.status,
            responseError: errorData,
          }
        );
        return;
      }
      const data = await response.json();
      // Optionally handle success (e.g., navigate or show success)
      setIsDialogOpen(false);
      setOtp("");
      if (data?.data?.sessionUrl) {
        window.location.href = data.data.sessionUrl;
      } else {
          window.location.href = data.data.sessionUrl;
        // if failed then normal digio flow.
        // Show success message or redirect to another page
      }

      console.log("OTP submitted successfully", response);
    } catch (error) {
      handleAndTrackError(error, {
        apiPath: "/ekyc/submit-ckyc-otp",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendOtp = async () => {
    try {
      setIsResending(true);
      if (isFexprime) return await fexprimeResendOtp();
      if (!requestId || !ckycResponseRequestId) return;
      await fetch(`${config.API_URL}/ekyc/resend-ckyc-otp`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem(
            config.LOCAL_KEYS.SIGNUP_TOKEN
          )}`,
        },
        body: JSON.stringify({
          request_id: requestId,
          ckyc_response_request_id: ckycResponseRequestId,
        }),
      });
      // Optionally show a message that OTP was resent
    } catch (error) {
      handleAndTrackError(error, {
        apiPath: "/ekyc/resend-ckyc-otp",
      });
    } finally {
      setIsResending(false);
    }
  };

  const handleOtpCancel = () => {

    setIsDialogOpen(false);
    setOtp("");
  };

  return (
    <Div100vh
      className={`flex flex-col justify-between gap-4 px-8 py-6 ${
        theme?.backgroundColor && theme.backgroundColor
      }`}
    >
      <div className="flex flex-col gap-1">
        <Header
          heading="We've fetched your details"
          subheading="Please verify it's you"
          showChat={false}
        />
        <span
          className={`text-3xs ${theme?.darkTextColor && theme.darkTextColor}`}
        >
          CKYC No: {ckyc_ref_id}
        </span>
      </div>
      <div className="relative flex flex-col gap-4 border border-gray-500 rounded-md pt-8 pb-4 px-4 text-xs">
        <div className="absolute -top-10 left-1/2 transform -translate-x-1/2">
          <img
            alt="image-user"
            src={`data:image/png;base64, ${photo}`}
            className="w-20 h-20 object-cover rounded-full border"
          />
        </div>
        <KYCDetail
          icon={SquareUser}
          label="Name"
          value={name || "NA"}
          theme={theme}
        />
        <KYCDetail
          icon={SquareUser}
          label="Father Name"
          value={father_name || "NA"}
          theme={theme}
        />
        <KYCDetail
          icon={CalendarDays}
          label="Age"
          value={age || "NA"}
          theme={theme}
        />
        {/* <KYCDetail
          icon={MapPin}
          label="Correspondence Address"
          value={
            mergeAddress(corres_line1, corres_line2) + ", " + corres_pin || "NA"
          }
          theme={theme}
        />
        <KYCDetail
          icon={MapPinHouse}
          label="Permanent Address"
          value={mergeAddress(perm_line1, perm_line2) + ", " + perm_pin || "NA"}
          theme={theme}
        /> */}
      </div>
      <div className="flex gap-2 justify-between text-sm">
        <button
          className={`w-full rounded-md font-semibold px-4 py-3 ${
            isLoading
              ? "bg-gray-300 text-gray-500 cursor-not-allowed"
              : theme?.primaryButtonBgColor
              ? `${theme?.darkTextColor} border ${theme?.borderColor}`
              : "bg-blue-900 text-white"
          }`}
          onClick={handleClickNo}
          disabled={isLoading}
        >
          No, it's not me
        </button>
        <button
          className={`border w-full rounded-md font-semibold px-4 py-3 transition-all ${
            isYesLoading
              ? "bg-gray-300 text-gray-500 cursor-not-allowed"
              : theme?.primaryButtonBgColor
              ? `${theme?.primaryButtonBgColor} ${theme?.primaryButtonTextColor}`
              : "bg-white text-blue-900"
          }`}
          onClick={handleClickYes}
          disabled={isYesLoading}
        >
          {isYesLoading ? "Loading..." : "Yes, it's me"}
        </button>
      </div>
      <Dialog
        open={isDialogOpen}
        onClose={handleOtpCancel}
        otp={otp}
        setOtp={setOtp}
        onSubmit={handleOtpSubmit}
        onResend={handleResendOtp}
        isResending={isResending}
        isSubmitting={isSubmitting}
        message={dialogMessage}
      />
    </Div100vh>
  );
}
